import React, { Fragment, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import NumericInput from 'react-numeric-input';
import { toast } from 'react-toastify';
import { IMaskInput } from 'react-imask';

import Quill from '../../common/Quill';
import InputWrapper from '../../common/InputWrapper';
import Select from '../../common/Select';
import Checkbox from '../../common/Checkbox';
import Button from '../../common/Button';
import SpinnerBlock from '../../common/SpinnerBlock';
import TextArea from '../../common/TextArea';
import QuantityPicker from './../../common/QuantityPicker';
import QuestionMark from '../../common/QuestionMark';

import { showModal } from '../../../actions/modal';
import { fetchGetAccountingAttributes, fetchAddAccountingAttribute } from '../../../actions/items';

import { VAT_RATES, PRODUCT_TYPES, ITEMS_PAGE_TABS } from '../../../config';
import { checkIsInteger } from '../../../helpers';
import CategoriesListBlock from './../common/CategoriesListBlock';
import ChestnyZnakInfoBlock from '../common/ChestnyZnakInfoBlock';
import { POSSIBLE_MASKS } from '../../../helpers';
import { useAutoTranslation } from '../../../helpers/useAutoTranslation';

const Wrapper = styled('div')`
  position: relative;
  padding: 1.5rem 0;
  > div:not(.border-block) {
    margin-left: calc(1.5rem - 12px);
    margin-right: calc(1.5rem - 12px);
  }
  .border-block {
    border-top: 1px solid #f7f6f6;
  }
  
  .composition {
    position: relative;

    textarea {
      min-height: 120px;
      padding: 0.6rem 0.9rem 0.9rem;
    }

    .text-length {
      position: absolute;
      color: #AEB5CB;
      bottom: 1px;
      font-weight: 400;
      font-size: 8px;
      line-height: 12px;
      left: 15px;
      background: white;
      padding-bottom: 4px;
      width: calc(100% - 40px);
    }
  }
  
  .batch-col {
    max-width: 450px;
    padding: 0 12px;
    width: 100%;
  }
  
  .btn-stock {
    i.fe-eye {
      margin-right: 5px;
      color: #7e57c2; 
      fontSize: 14px;
    }
  }
  
  .number-input {
    input[type="number"]::-webkit-outer-spin-button,
    input[type="number"]::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    input[type="number"] {
      -moz-appearance: textfield;
    }
    input[type="number"]:hover,
    input[type="number"]:focus {
      -moz-appearance: number-input;
    }
    
  }
  
  .green-radio {
    .custom-control-input:checked ~ .custom-control-label::before {
      color: #93B454;
      border-color: #93B454;
      background-color: #93B454;
    }
    .custom-radio .custom-control-input:disabled:checked ~ .custom-control-label::before {
      background-color: rgba(0, 123, 255, 0.5);
      border-color: #adb5bd;
    }
  }
  
  h4.label {
    margin: 0 0 0.8rem;
    font-weight: 500;
    font-size: 13px;
    color: #72747b;
    line-height: 1.5;
  }
  
  @media (max-width: 768px) {
    .batch-col {
      padding: 0;
    }
    .batch-block {
      margin-left: 0 !important;
      margin-right: 0 !important;
    }
  }
}


.parameters-checkboxes {
  display: flex;
}

@media (max-width: 530px) {
  .parameters-checkboxes {
    flex-direction: column;
    .custom-checkbox:not(:first-child) {
      margin-top: 10px;
    }
  }
}

.chz-info {
  margin-top: 15px;
}

.spent-chz {
  margin-left: 30px;
}
`;

const getAttributeValue = (attribute) => {
  if (attribute.stringValue) {
    return attribute.stringValue;
  }
  if (attribute.decimalValue) {
    return attribute.decimalValue;
  }
  return '';
};

const getActualAttributesOptions = (attributes, additionalDescsOptions) => {
  let actualOptions = attributes ? attributes.map((a) => ({ value: a.code, label: a.title })) : [];
  if (additionalDescsOptions && additionalDescsOptions.length) {
    actualOptions = actualOptions.concat(
      additionalDescsOptions.map((o) => ({ value: o.title, label: o.title, isText: true })),
    );
  }
  return actualOptions;
};

const AttributeItem = (props) => {
  const {
    attributes,
    index,
    code,
    handleAttributeValueChange,
    handleRemoveAttribute,
    WBCategories,
  } = props;

  const { t } = useAutoTranslation();

  const currentAttribute = attributes.find((f) => f.code === code);

  if (!code || !currentAttribute) return null;

  return (
    <div className="row attribute-row">
      <div className="col-3 title-block" style={{ maxWidth: '145px' }}>
        <div style={{ fontWeight: 500 }}>{currentAttribute.title}</div>
        <div>{code}</div>
      </div>
      <div className="col-8 tags-block">
        {currentAttribute.type === 'decimal' && (
          <div className="form-group" style={{ marginTop: 0, marginBottom: 0 }}>
            <InputWrapper
              type="text"
              noLabel
              id={`${code}Value`}
              value={getAttributeValue(props)}
              onChange={(valueAsNumber) => handleAttributeValueChange(valueAsNumber, index)}
              placeholder={t('Введите значение')}
              component={NumericInput}
              style={false}
            />
          </div>
        )}
        {currentAttribute.type === 'string' && currentAttribute.code !== 'wbtype' && (
          <div className="form-group" style={{ marginTop: '10px', marginBottom: 0 }}>
            <InputWrapper
              type="text"
              noLabel
              id={`${code}Value`}
              value={getAttributeValue(props)}
              onChange={(e) => handleAttributeValueChange(e.target.value, index)}
              placeholder={t('Введите значение')}
              maxLength="50"
            />
            <div style={{ fontSize: '10px' }}>{t('Максимум 50 символов')}</div>
          </div>
        )}
      </div>
      <div className="col-1 removing-block" style={{ display: 'flex', paddingRight: '0' }}>
        <Button
          style={{ marginLeft: 'auto' }}
          classType="btn-link"
          type="button"
          text={<i className="fe-trash-2" style={{ color: '#6c757d', fontSize: '20px' }} />}
          onClick={() => handleRemoveAttribute(index)}
        />
      </div>
    </div>
  );
};

const PropertiesBlock = (props) => {
  const {
    values,
    errors,
    attributes,
    additionalDescsOptions,
    handleShowStockByBatch,
    handleChangeInput,
    addNewAttribute,
    isNew,
    currentProduct,
  } = props;

  const dispatch = useDispatch();
  const { countries: countriesFromRedux } = useSelector((state) => state.settings);

  const { t } = useAutoTranslation();

  const [countries, setCountries] = useState([]);
  const [WBCategories, setWBCategories] = useState();

  const [accountingAttributes, setAccountingAttributes] = useState([]);
  const [showAccountingAttributes, setShowAccountingAttributes] = useState(false);
  const [showExportData, setShowExportData] = useState(false);
  const [showBatches, setShowBatches] = useState(false);
  const [showExpirationMonthsLimitData, setShowExpirationMonthsLimitData] = useState(false);
  const [showManufacturedData, setshowManufacturedData] = useState(false);
  const [showComposition, setShowComposition] = useState(false);
  const [showWarrantyPeriod, setShowWarrantyPeriod] = useState(false);
  const [showExpirationDaysLimit, setShowExpirationDaysLimit] = useState(false);

  const [loading, setLoading] = useState(false);

  const [currentMasks, setCurrentMasks] = useState('');
  const [isRegExpValid, setIsRegExpValid] = useState({});

  const [flagEAC, setFlagEac] = useState();

  useEffect(() => {
    setFlagEac(values.stickerEAC);
  }, [values.stickerEAC]);

  const handleStickerEAC = (checked) => {
    handleChangeInput('stickerEAC', checked);
  };

  useEffect(() => {
    (async function () {
      setLoading(true);
      const newAccountingAttributes = await dispatch(fetchGetAccountingAttributes());
      setAccountingAttributes(newAccountingAttributes);
      setCountries(countriesFromRedux.map((c) => ({ label: c.rusName, value: c.code })));
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!showExportData && (values.tnvedCode || values.internationalDescription)) {
      setShowExportData(true);
    }
  }, [values.tnvedCode, values.internationalDescription]);

  useEffect(() => {
    if (
      !showAccountingAttributes &&
      values.accountingAttributes &&
      values.accountingAttributes.length
    ) {
      setShowAccountingAttributes(true);
    }
  }, [values.accountingAttributes]);

  useEffect(() => {
    if (values.expirationDaysLimit) {
      setShowExpirationDaysLimit(true);
    }
  }, [values.expirationDaysLimit]);

  useEffect(() => {
    if (values.warranty) setShowWarrantyPeriod(true);
  }, [values.warranty]);

  const defineDefaultMaskType = (attributeStringValue, idx, attributeCode) => {
    if (!attributeStringValue) return null;
    if (currentMasks[idx]) return currentMasks[idx];

    if (attributeStringValue.includes('{УИТ}')) {
      setCurrentMasks({
        ...currentMasks,
        [idx]: POSSIBLE_MASKS[0],
      });
      return POSSIBLE_MASKS[0];
    }

    if (
      attributeStringValue.includes('/') ||
      attributeStringValue.includes('\\') ||
      attributeStringValue.includes('@') ||
      attributeStringValue.includes('+') ||
      attributeStringValue.includes('*') ||
      attributeStringValue.includes('[') ||
      attributeStringValue.includes('(') ||
      attributeStringValue.includes('{') ||
      attributeStringValue.includes('$')
    ) {
      setCurrentMasks({
        ...currentMasks,
        [idx]: POSSIBLE_MASKS[2],
      });
      return POSSIBLE_MASKS[2];
    }

    if (attributeStringValue.includes('?')) {
      setCurrentMasks({
        ...currentMasks,
        [idx]: POSSIBLE_MASKS[1],
      });
      return POSSIBLE_MASKS[1];
    }

    setCurrentMasks({
      ...currentMasks,
      [idx]: { value: 'type_undefined', label: t('Тип маски не определён!') },
    });
    return { value: 'type_undefined', label: t('Тип маски не определён!') };
  };

  const handleChangeMask = (option, idx) => {
    setCurrentMasks({
      ...currentMasks,
      [idx]: option,
    });

    if (option.value === POSSIBLE_MASKS[0].value) {
      handleAccountingAttributeValueChange('{УИТ}', idx);
    }
  };

  const convertQuestionSignsQtyToNumber = (signs, idx) => {
    if (!signs) {
      handleLengthCheckMaskValue(1, idx);
      return 1;
    } else {
      return signs.length;
    }
  };

  const convertNumberToQuestionSignsQty = (number) => {
    let questions = '';
    for (let i = 0; i < number; i++) {
      questions += '?';
    }
    return questions;
  };

  const handleAddAttribute = () => {
    const newAttributes = (values.attributes ? values.attributes : []).slice();
    newAttributes.push({});
    handleChangeInput('attributes', newAttributes);
  };

  const handleAddWBAttribute = () => {
    const newAttributes = (values.attributes ? values.attributes : []).slice();
    newAttributes.push({ code: `wbtype`, title: t(`Категория товара wb`), type: `string` });
    handleChangeInput('attributes', newAttributes);
  };

  const handleSetNewAttribute = (data) => {
    let currentAttribute = null;
    const isNewAttribute = data.__isNew__;

    if (isNewAttribute) {
      currentAttribute = {
        id: uuidv4(),
        code: true,
        values: [],
        title: data.label,
        type: 'string',
      };

      dispatch(
        showModal('EditAttribute', {
          attribute: currentAttribute,
          submitCallback: async (newData) => {
            if (newData.type !== 'text') {
              currentAttribute = newData;
              await addNewAttribute(currentAttribute);
              updateAttributes(currentAttribute);
            } else {
              handleUpdateAdditionalDescriptions(newData);
            }
          },
        }),
      );
      return false;
    } else {
      if (data.isText) {
        handleUpdateAdditionalDescriptions({ title: data.label });
        return;
      }
      const attributeId = data.value;
      currentAttribute = attributes && attributes.find((f) => f.code === attributeId);
    }

    if (currentAttribute) {
      updateAttributes(currentAttribute);
    }

    function updateAttributes(currentAttribute) {
      const newAttributes = values.attributes.slice();
      newAttributes[newAttributes.length - 1] = {
        ...currentAttribute,
        values: currentAttribute.values || [],
        isNew: true,
      };
      handleChangeInput('attributes', newAttributes);
      //if(currentFeature.values) setUnsavedStatus(true);
    }
  };

  const handleAttributeValueChange = (value, index) => {
    const newAttributes = values.attributes.slice();
    const currentDictionatyItem =
      attributes && attributes.find((f) => f.code === newAttributes[index].code);
    if (currentDictionatyItem.type === 'string') {
      newAttributes[index].stringValue = value;
    } else {
      newAttributes[index].decimalValue = value;
    }
    handleChangeInput('attributes', newAttributes);
  };

  const handleRemoveAttribute = (index) => {
    const newAttributes = values.attributes.slice();
    newAttributes.splice(index, 1);
    handleChangeInput('attributes', newAttributes);
  };

  const handleUpdateAdditionalDescriptions = (data) => {
    handleRemoveAttribute(values.attributes.length - 1);
    const newAdditionalDescriptions = values.additionalDescriptions
      ? values.additionalDescriptions.slice()
      : [];
    newAdditionalDescriptions.push({ title: data.title, description: '' });
    handleChangeInput('additionalDescriptions', newAdditionalDescriptions);
  };

  const handleAdditionalDescriptionValueChange = (value, index) => {
    const newAdditionalDescriptions = values.additionalDescriptions.slice();
    newAdditionalDescriptions[index].description = value;
    handleChangeInput('additionalDescriptions', newAdditionalDescriptions);
  };

  const handleRemoveAdditionalDescription = (index) => {
    const newAdditionalDescriptions = values.additionalDescriptions.slice();
    newAdditionalDescriptions.splice(index, 1);
    handleChangeInput('additionalDescriptions', newAdditionalDescriptions);
  };

  const fetchNewAccountingAttribute = (attribute) => {
    const newAttribute = Object.assign({}, attribute);
    newAttribute.values = [];
    return dispatch(fetchAddAccountingAttribute(newAttribute)).then((result) => {
      toast.info(`${t('Атрибут')} "${result.code}" ${t('успешно добавлен')}`);
    });
  };

  const handleAddAccountingAttribute = () => {
    const newAccountingAttributes = (
      values.accountingAttributes ? values.accountingAttributes : []
    ).slice();
    newAccountingAttributes.push({});
    handleChangeInput('accountingAttributes', newAccountingAttributes);
  };

  const handleLengthCheckMaskValue = (currentValue, idx, min, max) => {
    if (currentValue < min) currentValue = min;
    if (currentValue > max) currentValue = max;

    let convertedValue = convertNumberToQuestionSignsQty(currentValue);
    handleAccountingAttributeValueChange(convertedValue, idx);
  };

  const isRegularExpressionValid = (regexp) => {
    if (regexp === '*') return true;
    if (regexp === '{УИТ}') return false;

    const reg = '/' + regexp + '/';
    try {
      const m = reg.match(/^([/~@;%#'])(.*?)\1([gimsuy]*)$/);
      return m ? Boolean(new RegExp(m[2], m[3]) || new RegExp(regexp)) : false;
    } catch (e) {
      return false;
    }
  };

  const handleRegExpValidStatus = (isValid, idx) => {
    setIsRegExpValid({
      ...isRegExpValid,
      [idx]: isValid,
    });
  };

  const defineRegExpValidStatus = (value, idx, attributeCode) => {
    const key = attributeCode + idx;
    if (isRegExpValid.hasOwnProperty(key)) return;

    const isValid = isRegularExpressionValid(value);
    handleRegExpValidStatus(isValid, key);
    return isValid;
  };

  const handleRegExpMaskValue = (currentValue, idx, attributeCode) => {
    const key = attributeCode + idx;

    isRegularExpressionValid(currentValue)
      ? handleRegExpValidStatus(true, key)
      : handleRegExpValidStatus(false, key);

    handleAccountingAttributeValueChange(currentValue, idx);
  };

  const handleAccountingAttributeValueChange = (value, index) => {
    const newAccountingAttributes = values.accountingAttributes.slice();
    newAccountingAttributes[index].stringValue = value;
    handleChangeInput('accountingAttributes', newAccountingAttributes);
  };

  const handleRemoveAccountingAttribute = (index) => {
    const newAccountingAttributes = values.accountingAttributes.slice();
    newAccountingAttributes.splice(index, 1);
    handleChangeInput('accountingAttributes', newAccountingAttributes);
  };

  const handleSetNewAccountingAttribute = (data) => {
    let currentAttribute = null;
    const isNewAttribute = data.__isNew__;

    if (isNewAttribute) {
      currentAttribute = {
        id: uuidv4(),
        code: true,
        values: [],
        title: data.label,
      };
      dispatch(
        showModal('EditAccountingAttribute', {
          attribute: currentAttribute,
          submitCallback: async (newData) => {
            currentAttribute = newData;
            await fetchNewAccountingAttribute(currentAttribute);
            updateAttributes(currentAttribute);
          },
        }),
      );
      return false;
    } else {
      const attributeId = data.value;
      currentAttribute =
        accountingAttributes && accountingAttributes.find((f) => f.code === attributeId);
    }

    if (currentAttribute) {
      updateAttributes(currentAttribute);
    }

    function updateAttributes(currentAttribute) {
      const newAccountingAttributes = values.accountingAttributes.slice();
      newAccountingAttributes[newAccountingAttributes.length - 1] = {
        ...currentAttribute,
        values: currentAttribute.values || [],
        isNew: true,
      };
      handleChangeInput('accountingAttributes', newAccountingAttributes);
    }
  };

  const handleChangeWarrantyPeriod = (value) => {
    if (value < 0) value = 0;
    handleChangeInput('warranty', value);
  };

  const actualAttributes = getActualAttributesOptions(attributes, additionalDescsOptions);
  const newAttributesForAdding = values.attributes && values.attributes.filter((a) => !a.code);

  const actualAccountingAttributes = accountingAttributes
    ? accountingAttributes.map((a) => ({ value: a.code, label: a.title }))
    : [];
  const newAccountingAttributesForAdding =
    values.accountingAttributes && values.accountingAttributes.filter((a) => !a.code);

  const accountingExpirationData = [
    { label: t(`Не учитывать`), value: `NO_EXPIRATION` },
    { label: t(`Учитывать`), value: `USE_EXPIRATION` },
  ];

  const handleChangeBatchAccounting = (checked) => {
    handleChangeInput('useBatchAccounting', checked);
    if (!checked) {
      handleChangeInput('expirationMode', 'NO_EXPIRATION');
      handleChangeInput('expirationDaysLimit', null);
      return;
    }
    handleChangeInput('expirationMode', 'USE_EXPIRATION');
  };

  const handleChangeExpirationMode = (val) => {
    handleChangeInput('expirationMode', val);
    if (val === 'NO_EXPIRATION') {
      handleChangeInput('expirationDaysLimit', null);
      handleChangeInput('useBatchAccounting', false);
      return;
    }
    handleChangeInput('useBatchAccounting', true);
  };

  const handleChangeExpirationDaysLimit = (value) => {
    handleChangeInput('expirationDaysLimit', value);
  };

  const handleChangeExpirationMonthsInput = (val) => {
    if (!checkIsInteger(val)) {
      handleChangeInput('expirationMonthsLimit', values.expirationMonthsLimit ?? null);
      return;
    }

    handleChangeInput('expirationMonthsLimit', val);
  };

  useEffect(() => {
    if (!showComposition && values.composition) {
      setShowComposition(true);
    }
  }, [values.composition]);

  useEffect(() => {
    if (!showManufacturedData && values.manufacturedBy) {
      setshowManufacturedData(true);
    }
  }, [values.manufacturedBy]);

  useEffect(() => {
    if (!showExpirationMonthsLimitData && values.expirationMonthsLimit) {
      setShowExpirationMonthsLimitData(true);
    }
  }, [values.expirationMonthsLimit]);

  useEffect(() => {
    if (!showBatches && values.useBatchAccounting) {
      setShowBatches(true);
    }
  }, [values.useBatchAccounting]);

  const { account } = useSelector((state) => state.auth);

  const NEW_PRODUCT_TYPES =
    account.usePackingMaterials === true
      ? PRODUCT_TYPES
      : PRODUCT_TYPES.slice(0, PRODUCT_TYPES.length - 3); // Если аккаунт использует usePackingMaterial, то показываем вкладку

  return (
    <Wrapper className="card-box" style={{ height: 'auto' }}>
      <SpinnerBlock show={loading} />
      <div className="row">
        <div className="col-12">
          <h4 className="header-title">{t('Свойства')}</h4>
        </div>
      </div>

      <div className="row">
        <div className="col-6">
          <div className="form-group">
            <Select
              label={t('Тип продукта')}
              id="type"
              options={NEW_PRODUCT_TYPES}
              value={NEW_PRODUCT_TYPES.find((o) => o.value === values.type)}
              onChange={({ value }) => handleChangeInput('type', value)}
              isDisabled={!isNew}
            />
          </div>
        </div>
        <div className="col-6">
          <div className="form-group">
            <Select
              label={t('Ставка НДС')}
              id="vat"
              options={VAT_RATES}
              value={VAT_RATES.find((o) => o.value === values.vat)}
              onChange={({ value }) => handleChangeInput('vat', value)}
              noOptionsMessage={t('Атрибуты не найдены')}
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-6 brand">
          <div className="form-group">
            <InputWrapper
              type="text"
              label={t('Бренд')}
              id="brandName"
              value={values.brandName}
              error={errors.brandName}
              onChange={(e) => handleChangeInput('brandName', e.target.value)}
              placeholder={t('Введите название бренда')}
            />
          </div>
        </div>
        <div className="col-6 country">
          <div className="form-group">
            <Select
              label={t('Страна происхождения')}
              isSearchable
              id="countryOfOrigin"
              value={countries.find((o) => o.value === values.countryOfOrigin)}
              options={countries}
              onChange={({ value }) => handleChangeInput('countryOfOrigin', value)}
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-6">
          <div className="form-group">
            <CategoriesListBlock
              currentProduct={currentProduct}
              handleChangeInput={handleChangeInput}
            />
          </div>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-12">
          <Checkbox
            label={t('Запрет авиадоставки')}
            id="isNoAvia"
            checked={values.noAvia}
            onChange={(e) => handleChangeInput('noAvia', e.target.checked)}
            wrapperStyle={{ marginRight: '30px' }}
          />
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-12">
          <div className="form-group parameters-checkboxes">
            <Checkbox
              label={t('Требуется обрешётка')}
              id="boxNeeded"
              checked={values.boxNeeded}
              onChange={(e) => handleChangeInput('boxNeeded', e.target.checked)}
              wrapperStyle={{ marginRight: '30px' }}
            />
            <Checkbox
              label={t('Хрупкий товар')}
              id="fragile"
              checked={values.fragile}
              onChange={(e) => handleChangeInput('fragile', e.target.checked)}
              wrapperStyle={{ marginRight: '30px' }}
            />
            <Checkbox
              label={t('ЕАС стикер товара')}
              id="stickerEAC"
              checked={values.stickerEAC}
              onChange={(e) => handleStickerEAC(e.target.checked)}
              wrapperStyle={{ marginRight: '30px' }}
            />
          </div>
        </div>
      </div>
      {flagEAC && (
        <div className="row">
          <div className="col-6 eac-certification-type">
            <div className="form-group">
              <InputWrapper
                type="text"
                label={t('Тип сертификации ЕАС')}
                id="eacCertificationType"
                value={values.eacCertificationType}
                error={errors.eacCertificationType}
                onChange={(e) => handleChangeInput('eacCertificationType', e.target.value)}
                placeholder={t('Тип сертификации ЕАС')}
              />
            </div>
          </div>
          <div className="col-6 supplier">
            <div className="form-group">
              <InputWrapper
                type="text"
                label={t('Поставщик')}
                id="supplier"
                value={values.supplier}
                error={errors.supplier}
                onChange={(e) => handleChangeInput('supplier', e.target.value)}
                placeholder={t('Поставщик')}
              />
            </div>
          </div>
        </div>
      )}

      <div className="border-block" style={{ marginTop: '24px' }} />

      <div className="row">
        <div className="col-12" style={{ display: 'flex', marginTop: '35px' }}>
          <h4 className="header-title" style={{ marginBottom: '7px' }}>
            {t('Атрибуты')}
          </h4>
          <div style={{ display: 'flex', marginLeft: 'auto' }}>
            <Checkbox
              label={t('Хит')}
              id="isBestseller"
              checked={values.isBestseller}
              onChange={(e) => handleChangeInput('isBestseller', e.target.checked)}
              wrapperStyle={{ marginRight: '30px' }}
            />
            <Checkbox
              label={t('Новинка')}
              id="isNovelty"
              checked={values.isNovelty}
              onChange={(e) => handleChangeInput('isNovelty', e.target.checked)}
            />
          </div>
        </div>
      </div>

      {values.attributes &&
        values.attributes.map((attribute, i) => (
          <AttributeItem
            key={`attribute-${i}`}
            {...attribute}
            index={i}
            WBCategories={WBCategories}
            attributes={attributes}
            handleAttributeValueChange={handleAttributeValueChange}
            handleRemoveAttribute={handleRemoveAttribute}
          />
        ))}

      {values.additionalDescriptions &&
        values.additionalDescriptions.map((ad, i) => (
          <div className="row attribute-row" key={`ad-${i}`}>
            <Fragment>
              <div className="col-3 title-block" style={{ maxWidth: '145px' }}>
                <div style={{ fontWeight: 500 }}>{ad.title}</div>
                {/* <div>{ad.code}</div> */}
              </div>
              <div className="col-8 tags-block">
                <Quill
                  id={`ad-${i}`}
                  value={values.additionalDescriptions[i].description}
                  onChange={(value) => handleAdditionalDescriptionValueChange(value, i)}
                  placeholder={t('Введите описание')}
                />
              </div>
              <div className="col-1 removing-block" style={{ display: 'flex', paddingRight: '0' }}>
                <Button
                  style={{ marginLeft: 'auto' }}
                  classType="btn-link"
                  type="button"
                  text={<i className="fe-trash-2" style={{ color: '#6c757d', fontSize: '20px' }} />}
                  onClick={() => handleRemoveAdditionalDescription(i)}
                />
              </div>
            </Fragment>
          </div>
        ))}

      {newAttributesForAdding
        ? newAttributesForAdding.map((attribute, i) => (
            <div className="row attribute-row" key={`new-attribute-${i}`}>
              <div className="col-12" style={{ paddingLeft: '0' }}>
                <div className="form-group" style={{ maxWidth: '250px', margin: '10px 0' }}>
                  <Select
                    isCreatable
                    placeholder={`${t('Введите имя атрибута')}...`}
                    options={actualAttributes}
                    value={
                      attribute.title
                        ? { value: attribute.code, label: attribute.title }
                        : undefined
                    }
                    menuPortalTarget={document.querySelector('body')}
                    onChange={(data) => handleSetNewAttribute(data, i)}
                  />
                </div>
              </div>
            </div>
          ))
        : null}

      <div className="row add-option-button-row ml-3">
        <div className="col-12" style={{ paddingLeft: 0 }}>
          <button type="button" className="btn btn-sm btn-light mb-2" onClick={handleAddAttribute}>
            <i className="fe-plus" /> {t('Добавить атрибут')}
          </button>
        </div>
      </div>

      <div className="border-block" style={{ marginTop: '18px', marginBottom: '26px' }} />

      {!showBatches ? (
        <div className="row">
          <div className="col-12">
            <div
              style={{ color: '#7e57c2', cursor: 'pointer' }}
              onClick={() => setShowBatches(true)}
            >
              {t('Добавить атрибуты партионного учёта')}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="row">
            <div className="col-12">
              <h4 className="header-title">{t('Атрибуты партионного учёта')}</h4>
            </div>
          </div>

          <div className="d-flex flex-column flex-md-row batch-block mt-1 mb-1">
            <div className="batch-col">
              <div className="form-group">
                <Checkbox
                  label={t('Включить учёт по партиям')}
                  id="useBatchAccounting"
                  checked={values.useBatchAccounting}
                  onChange={(e) => handleChangeBatchAccounting(e.target.checked)}
                  wrapperStyle={{ marginRight: '30px' }}
                />
              </div>
              <div className="form-group">
                <Checkbox
                  label={t('Вести учёт сроков годности в партиях')}
                  id="expirationMode"
                  checked={values.expirationMode === 'USE_EXPIRATION' ? true : false}
                  onChange={(e) =>
                    handleChangeExpirationMode(
                      e.target.checked ? 'USE_EXPIRATION' : 'NO_EXPIRATION',
                    )
                  }
                  wrapperStyle={{ marginRight: '30px' }}
                />
              </div>
            </div>
          </div>
          <div className="row">
            <Button
              onClick={() => handleShowStockByBatch(false)}
              classType="btn-link btn-stock"
              type="button"
              text={
                <span>
                  <i className="fe-eye" />
                  {t('Посмотреть остатки по партиям')}
                </span>
              }
            />
          </div>
        </>
      )}

      <div className="border-block" style={{ marginTop: '18px', marginBottom: '18px' }} />

      {!showAccountingAttributes ? (
        <div className="row">
          <div className="col-12">
            <div
              style={{ color: '#7e57c2', cursor: 'pointer' }}
              onClick={() => setShowAccountingAttributes(true)}
            >
              {t('Добавить учет по уникальным номерам')}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="row">
            <div className="col-12" style={{ display: 'flex', marginTop: '35px' }}>
              <h4 className="header-title" style={{ marginBottom: '7px' }}>
                {t('Учёт товара по уникальным номерам при отгрузке')}
              </h4>
            </div>
          </div>
          {values.accountingAttributes.map((attribute, i) =>
            attribute.code ? (
              <div className="row attribute-row" key={`accounting-attribute-${i}`}>
                {attribute.code ? (
                  <>
                    <div className="col-3 title-block" style={{ maxWidth: '145px' }}>
                      <div style={{ fontWeight: 500 }}>
                        {(accountingAttributes.find((f) => f.code === attribute.code) || {}).title}
                      </div>
                      <div>{attribute.code}</div>
                    </div>
                    <div className="col-8 tags-block">
                      <div className="form-group" style={{ marginTop: '10px', marginBottom: 0 }}>
                        <div>
                          <Select
                            options={POSSIBLE_MASKS}
                            defaultValue={defineDefaultMaskType(
                              attribute.stringValue,
                              i,
                              attribute.code,
                            )}
                            onChange={(option) => handleChangeMask(option, i)}
                            placeholder={`${t('Выберите маску')}...`}
                          />
                          {currentMasks && currentMasks[i] ? (
                            <>
                              {currentMasks[i].value === 'type_undefined' && (
                                <InputWrapper
                                  type="text"
                                  value={attribute.stringValue}
                                  placeholder={t('Введите значение')}
                                  maxLength="50"
                                  readOnly="true"
                                />
                              )}
                              {currentMasks[i].value === POSSIBLE_MASKS[1].value && (
                                <QuantityPicker
                                  className="w-25 mt-2"
                                  value={convertQuestionSignsQtyToNumber(attribute.stringValue, i)}
                                  onChange={(value) => handleLengthCheckMaskValue(value, i, 1, 200)}
                                  min={1}
                                  max={200}
                                  reverse
                                />
                              )}
                              {currentMasks[i].value === POSSIBLE_MASKS[2].value && (
                                <div className="d-flex align-items-center mt-2 mb-2">
                                  <InputWrapper
                                    type="text"
                                    noLabel
                                    value={attribute.stringValue}
                                    onChange={(e) =>
                                      handleRegExpMaskValue(e.target.value, i, attribute.code)
                                    }
                                    placeholder={t('Укажите регулярное выражение')}
                                    maxLength="200"
                                  />
                                  {attribute?.stringValue?.length > 0 &&
                                  (defineRegExpValidStatus(
                                    attribute?.stringValue,
                                    i,
                                    attribute.code,
                                  ) ||
                                    !isRegExpValid[`${attribute.code + i}`]) ? (
                                    <span className="error-invalid">
                                      {t('Регулярное выражение не валидно!')}
                                    </span>
                                  ) : null}
                                  <QuestionMark hint={t('Укажите регулярное выражение')} />
                                </div>
                              )}
                            </>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <div
                      className="col-1 removing-block"
                      style={{ display: 'flex', paddingRight: '0' }}
                    >
                      <Button
                        style={{ marginLeft: 'auto' }}
                        classType="btn-link"
                        type="button"
                        text={
                          <i
                            className="fe-trash-2"
                            style={{ color: '#6c757d', fontSize: '20px' }}
                          />
                        }
                        onClick={() => handleRemoveAccountingAttribute(i)}
                      />
                    </div>
                  </>
                ) : null}
              </div>
            ) : null,
          )}

          {newAccountingAttributesForAdding
            ? newAccountingAttributesForAdding.map((attribute, i) => (
                <div className="row attribute-row" key={`new-attribute-${i}`}>
                  <div className="col-12" style={{ paddingLeft: '0' }}>
                    <div className="form-group" style={{ maxWidth: '250px', margin: '10px 0' }}>
                      <Select
                        isCreatable
                        placeholder={t('Введите имя атрибута')}
                        options={actualAccountingAttributes}
                        value={
                          attribute.title
                            ? { value: attribute.code, label: attribute.title }
                            : undefined
                        }
                        menuPortalTarget={document.querySelector('body')}
                        onChange={(data) => handleSetNewAccountingAttribute(data, i)}
                      />
                    </div>
                  </div>
                </div>
              ))
            : null}

          <div className="row add-option-button-row">
            <div
              className="col-12"
              style={{
                paddingLeft:
                  values.accountingAttributes && values.accountingAttributes.length ? 0 : '12px',
              }}
            >
              <button
                type="button"
                className="btn btn-sm btn-light mb-2"
                onClick={handleAddAccountingAttribute}
              >
                <i className="fe-plus" />
                {t('Добавить')}
              </button>
              <ChestnyZnakInfoBlock
                handleChangeInput={handleChangeInput}
                allowSkipCis={values.allowSkipCis}
                currentProductVariants={currentProduct?.variants}
                currentProductId={currentProduct?.id}
                isProductPage={true}
              />
            </div>
          </div>
        </>
      )}

      <div className="border-block" style={{ marginTop: '18px', marginBottom: '18px' }} />

      {!showExportData ? (
        <div className="row">
          <div className="col-12">
            <div
              style={{ color: '#7e57c2', cursor: 'pointer' }}
              onClick={() => setShowExportData(true)}
            >
              {t('Добавить данные для экспорта')}
            </div>
          </div>
        </div>
      ) : (
        <Fragment>
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <InputWrapper
                  type="text"
                  label={t('Код ТНВЭД')}
                  id="tnvedCode"
                  value={values.tnvedCode}
                  error={errors.tnvedCode}
                  onChange={(e) => handleChangeInput('tnvedCode', e.target.value)}
                  placeholder={t('Введите код ТНВЭД')}
                />
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <InputWrapper
                  type="text"
                  label={t('Описание на английском')}
                  id="internationalDescription"
                  value={values.internationalDescription}
                  error={errors.internationalDescription}
                  onChange={(e) => handleChangeInput('internationalDescription', e.target.value)}
                  placeholder={t('Введите описание на английском')}
                />
              </div>
            </div>
          </div>
        </Fragment>
      )}

      <div className="border-block" style={{ marginTop: '18px', marginBottom: '18px' }} />

      {!showExpirationMonthsLimitData ? (
        <div className="row">
          <div className="col-12">
            <div
              style={{ color: '#7e57c2', cursor: 'pointer' }}
              onClick={() => setShowExpirationMonthsLimitData(true)}
            >
              {t('Добавить срок годности товара')}
            </div>
          </div>
        </div>
      ) : (
        <Fragment>
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <InputWrapper
                  label={t('Срок годности (мес)')}
                  id="expirationMonthsLimit"
                  value={values.expirationMonthsLimit}
                  onChange={(e) => handleChangeExpirationMonthsInput(e.target.value)}
                  placeholder={t('Введите срок годности')}
                />
              </div>
            </div>
          </div>
        </Fragment>
      )}

      <div className="border-block" style={{ marginTop: '18px', marginBottom: '18px' }} />

      {!showExpirationDaysLimit ? (
        <div className="row">
          <div className="col-12">
            <div
              style={{ color: '#7e57c2', cursor: 'pointer' }}
              onClick={() => setShowExpirationDaysLimit(true)}
            >
              {t('Добавить лимит остаточного срока годности товара')}
            </div>
          </div>
        </div>
      ) : (
        <Fragment>
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <InputWrapper
                  type="number"
                  label={t('Лимит остаточного срока годности, дней')}
                  id="expirationDaysLimit"
                  value={values.expirationDaysLimit}
                  onChange={(e) => handleChangeExpirationDaysLimit(e.target.value)}
                  placeholder={t('Введите лимит остаточного срока годности')}
                />
              </div>
            </div>
          </div>
        </Fragment>
      )}

      <div className="border-block" style={{ marginTop: '18px', marginBottom: '18px' }} />

      {!showManufacturedData ? (
        <div className="row">
          <div className="col-12">
            <div
              style={{ color: '#7e57c2', cursor: 'pointer' }}
              onClick={() => setshowManufacturedData(true)}
            >
              {t('Добавить производителя товара')}
            </div>
          </div>
        </div>
      ) : (
        <Fragment>
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <InputWrapper
                  type="text"
                  label={t('Производитель')}
                  id="manufacturedBy"
                  value={values.manufacturedBy}
                  error={errors.manufacturedBy}
                  onChange={(e) => handleChangeInput('manufacturedBy', e.target.value)}
                  placeholder={t('Введите производителя')}
                />
              </div>
            </div>
          </div>
        </Fragment>
      )}

      <div className="border-block" style={{ marginTop: '18px', marginBottom: '18px' }} />

      <div className="row">
        <div className="col-12">
          <div className="form-group">
            <InputWrapper
              type="text"
              label={t('ГОСТ')}
              id="gost"
              value={values.gost}
              error={errors.gost}
              onChange={(e) => handleChangeInput('gost', e.target.value)}
              placeholder={t('Введите ГОСТ')}
            />
          </div>
        </div>
      </div>

      <div className="border-block" style={{ marginTop: '18px', marginBottom: '18px' }} />

      {!showComposition ? (
        <div className="row">
          <div className="col-12">
            <div
              style={{ color: '#7e57c2', cursor: 'pointer' }}
              onClick={() => setShowComposition(true)}
            >
              {t('Добавить состав товара')}
            </div>
          </div>
        </div>
      ) : (
        <Fragment>
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <div className="composition">
                  <TextArea
                    label={t('Состав')}
                    id="composition"
                    value={values.composition}
                    error={errors.composition}
                    onChange={(e) => handleChangeInput('composition', e.target.value)}
                    placeholder={t('Введите состав')}
                    maxlength={2000}
                  />
                  <div className="text-length">{values.composition?.length ?? 0} / 2000</div>
                </div>
              </div>
            </div>
          </div>
        </Fragment>
      )}

      <div className="border-block" style={{ marginTop: '18px', marginBottom: '18px' }} />

      {!showWarrantyPeriod ? (
        <div className="row">
          <div className="col-12">
            <div
              style={{ color: '#7e57c2', cursor: 'pointer' }}
              onClick={() => setShowWarrantyPeriod(true)}
            >
              {t('Добавить срок гарантии товара')}
            </div>
          </div>
        </div>
      ) : (
        <Fragment>
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <InputWrapper
                  type="number"
                  label={t('Срок гарантии (мес)')}
                  id="warranty"
                  value={values.warranty}
                  onChange={(e) => handleChangeWarrantyPeriod(e.target.value)}
                  placeholder={t('Введите срок гарантии')}
                />
              </div>
            </div>
          </div>
        </Fragment>
      )}
    </Wrapper>
  );
};

export default PropertiesBlock;
